/**
 * auth.service.ts
 *
 * Session infrastructure ONLY. Login, signup, email verification, invitations
 * and password reset all live on the centralized SK Platform now — this app has
 * no local auth screens and simply redirects there when there's no valid
 * session (see navigation/platformAuthRedirect). What remains here is the
 * cookie-session plumbing the app itself needs:
 *   • me       — session probe used by the auth gate (cached, single-flight)
 *   • refresh  — silent access-token renewal
 *   • logout   — best-effort server-side teardown
 *
 * All silent by design (no toast): these are session infrastructure, not
 * user-initiated actions.
 */
import { apiClient } from '../api-client';
import { PLATFORM_AUTH } from '../../api-endpoints/api-endpoints';
import { MeResponse } from '../../schema/auth/me.schema';

/* ── /v1/me cache (5s TTL, single-flight) ─────────────────────────────────
 * The boot probe + post-action refreshes can fire several /me reads in a
 * burst; this collapses them into one network call. */
let _meCache: { data: MeResponse; at: number } | null = null;
let _meInflight: Promise<MeResponse> | null = null;
const ME_TTL = 5_000;

export const authService = {
  /** Best-effort session teardown. Never throws — logout must always proceed. */
  logout: async (): Promise<void> => {
    _meCache = null;
    try {
      await apiClient.post<void>(PLATFORM_AUTH.logout);
    } catch {
      /* swallow — the local session is cleared by the caller regardless */
    }
  },

  /** Silent access-token renewal (the api-client also refreshes on 401). */
  refresh: (): Promise<void> => apiClient.post<void>(PLATFORM_AUTH.refresh),

  /** Session probe used by the auth gate. Cached + single-flight; no toast. */
  me: (): Promise<MeResponse> => {
    if (_meCache && Date.now() - _meCache.at < ME_TTL) return Promise.resolve(_meCache.data);
    if (!_meInflight) {
      _meInflight = apiClient
        .get<MeResponse>(PLATFORM_AUTH.me)
        .then((d) => {
          _meCache = { data: d, at: Date.now() };
          return d;
        })
        .finally(() => {
          _meInflight = null;
        });
    }
    return _meInflight;
  },

  invalidateMeCache: () => {
    _meCache = null;
  },
};
